import { Controller } from "@hotwired/stimulus";
import { getJSON, listResults, formatDate } from "../api.js";

// Client payment history (staffpay#10c). Zero PII — renders only transactional
// fields from ClientPaymentSerializer; no lead contact data reaches the browser.

// ---------------------------------------------------------------------------
// Module-level helpers (pure, no state)
// ---------------------------------------------------------------------------

// Intl money formatter: "1234.50" → "1 234,50 zł".
const _moneyFmt = new Intl.NumberFormat("pl-PL", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatMoney(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return _moneyFmt.format(num) + " zł";
}

// Polish plural for "lead": 1 / 2–4 / 5+; 12–14 always take "leadów".
function pluralLeads(n) {
  if (n === 1) return "1 lead";
  const mod100 = n % 100;
  const mod10 = n % 10;
  if (mod100 >= 12 && mod100 <= 14) return `${n} leadów`;
  if (mod10 >= 2 && mod10 <= 4) return `${n} leady`;
  return `${n} leadów`;
}

// ---------------------------------------------------------------------------
// Controller
// ---------------------------------------------------------------------------

export default class extends Controller {
  static targets = ["rows", "status", "pageInfo", "prevBtn", "nextBtn", "pager"];

  static values = {
    listUrl: String,
  };

  // Private state
  #page = 1;
  #hasNextPage = false;

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  connect() {
    this.#loadList();
  }

  // -------------------------------------------------------------------------
  // Pagination actions
  // -------------------------------------------------------------------------

  prevPage() {
    if (this.#page > 1) {
      this.#page -= 1;
      this.#loadList();
    }
  }

  nextPage() {
    if (this.#hasNextPage) {
      this.#page += 1;
      this.#loadList();
    }
  }

  // -------------------------------------------------------------------------
  // Private — data loading
  // -------------------------------------------------------------------------

  async #loadList() {
    let data;
    try {
      data = await getJSON(this.#buildListUrl());
    } catch {
      this.#status("Nie udało się wczytać płatności.");
      return;
    }

    const results = listResults(data);
    this.rowsTarget.textContent = "";
    this.#renderPagination(data);

    if (!results.length) {
      this.#status("Nie masz jeszcze żadnych płatności.");
      return;
    }

    this.#status(null);
    const tmpl = document.getElementById("payments-row-template");
    if (!tmpl) return;

    for (const p of results) {
      const row = tmpl.content.firstElementChild.cloneNode(true);
      this.#fillRow(row, p);
      this.rowsTarget.appendChild(row);
    }
  }

  // -------------------------------------------------------------------------
  // Private — row rendering (no innerHTML — createElement + textContent only)
  // -------------------------------------------------------------------------

  #fillRow(row, p) {
    const f = (field) => row.querySelector(`[data-field="${field}"]`);

    // Date column
    const dateEl = f("date");
    if (dateEl) dateEl.textContent = formatDate(p.created);

    // What / whatSub
    const whatEl = f("what");
    if (whatEl) {
      if (p.kind === "order") {
        whatEl.textContent = `Pakiet #${p.order_code}`;
      } else {
        whatEl.textContent = "Doładowanie portfela";
      }
    }

    const whatSubEl = f("whatSub");
    if (whatSubEl) {
      if (p.kind === "order") {
        whatSubEl.textContent = pluralLeads(p.order_item_count ?? 0);
      } else {
        whatSubEl.textContent = "środki wpłacone";
      }
    }

    // Amount
    const amountEl = f("amount");
    if (amountEl) amountEl.textContent = formatMoney(p.amount_pln);

    // Method
    const methodEl = f("method");
    if (methodEl) {
      if (p.method === "p24") methodEl.textContent = "Przelewy24";
      else if (p.method === "wallet") methodEl.textContent = "Portfel";
      else methodEl.textContent = "Inna";
    }

    // Status pill — late=true overrides everything else
    const statusEl = f("status");
    if (statusEl) {
      const { text, pillClass } = this.#statusLabel(p);
      statusEl.textContent = text;
      statusEl.className = pillClass ? `ls-pill ${pillClass}` : "ls-pill";
    }

    // Refunds column
    const refundsEl = f("refunds");
    if (refundsEl) {
      refundsEl.textContent = this.#refundsText(p);
      // phone ledger hides empty meta (CSS [data-empty])
      refundsEl.toggleAttribute("data-empty", refundsEl.textContent === "—");
    }
  }

  // -------------------------------------------------------------------------
  // Private — label helpers
  // -------------------------------------------------------------------------

  #statusLabel(p) {
    if (p.late) {
      return { text: "Zwrot całej kwoty", pillClass: "ls-pill--refund_todo" };
    }
    if (p.status === "pending") {
      return { text: "Czeka na potwierdzenie", pillClass: "ls-pill--pending_p24" };
    }
    if (p.status === "paid") {
      return { text: "Opłacona", pillClass: "ls-pill--paid" };
    }
    if (p.status === "failed") {
      return { text: "Nieudana", pillClass: "ls-pill--failed" };
    }
    return { text: p.status ?? "—", pillClass: "" };
  }

  #refundsText(p) {
    const done = p.refunds_completed_pln ?? "0.00";
    const prog = p.refunds_in_progress_pln ?? "0.00";
    const hasDone = done !== "0.00";
    const hasProg = prog !== "0.00";

    if (hasDone && hasProg) {
      return `${formatMoney(done)} zwrócono · ${formatMoney(prog)} w trakcie`;
    }
    if (hasProg) {
      return `${formatMoney(prog)} · w trakcie`;
    }
    if (hasDone) {
      return `${formatMoney(done)} · zwrócono`;
    }
    return "—";
  }

  // -------------------------------------------------------------------------
  // Private — URL builder
  // -------------------------------------------------------------------------

  #buildListUrl() {
    if (this.#page > 1) {
      return `${this.listUrlValue}?page=${this.#page}`;
    }
    return this.listUrlValue;
  }

  // -------------------------------------------------------------------------
  // Private — UI helpers
  // -------------------------------------------------------------------------

  #renderPagination(data) {
    this.#hasNextPage = Boolean(data.next);
    const hasPrev = this.#page > 1;
    // nothing to page through when there are no payments at all
    if (this.hasPagerTarget) this.pagerTarget.hidden = (data.count ?? listResults(data).length) === 0;

    if (this.hasPageInfoTarget) {
      const count = data.count ?? listResults(data).length;
      this.pageInfoTarget.textContent = `strona ${this.#page} (${count} płatności)`;
    }
    if (this.hasPrevBtnTarget) this.prevBtnTarget.disabled = !hasPrev;
    if (this.hasNextBtnTarget) this.nextBtnTarget.disabled = !this.#hasNextPage;
  }

  #status(message) {
    if (!this.hasStatusTarget) return;
    this.statusTarget.hidden = message === null;
    if (message !== null) this.statusTarget.textContent = message;
  }
}
